import { useState, useEffect, useCallback } from 'react';
import { CloudOff, RefreshCw, Check, AlertCircle, Upload, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { 
  getSyncStatus, 
  getSyncQueue, 
  removeFromSyncQueue, 
  updateLastSyncTime,
  getConflicts,
  resolveConflict as resolveConflictInStorage,
  resolveAllConflicts as resolveAllConflictsInStorage,
  clearResolvedConflicts
} from '@/services/offlineDataService';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import API from '@/utils/api';
import ConflictResolutionDialog from '@/components/ConflictResolutionDialog';

/**
 * SyncStatusIndicator - Shows current sync status in the UI
 * 
 * Self-contained component that monitors online/offline status
 * and pending sync operations. Handles sync independently to avoid
 * circular dependency issues with hooks.
 * 
 * Features:
 * - Visual status indicator (icon + optional label)
 * - "Sync Now" button when there are pending changes
 * - Auto-sync when coming back online
 * - Conflict resolution dialog
 * 
 * States:
 * - Online & Synced (green check)
 * - Online & Syncing (spinning)
 * - Online & Pending (amber warning + Sync Now button)
 * - Online & Conflicts (red warning)
 * - Offline (gray cloud-off)
 */
const SyncStatusIndicator = ({ 
  className, 
  showLabel = false, 
  showSyncButton = false,
  size = 'sm',
  variant = 'icon' // 'icon' | 'button' | 'full'
}) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(() => getSyncStatus());
  const [conflicts, setConflicts] = useState(() => getConflicts().filter(c => !c.resolved));
  const [showConflictDialog, setShowConflictDialog] = useState(false);
  
  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Auto-sync when coming back online
      setTimeout(() => syncAll(true), 1000);
    };
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);
  
  // Refresh sync status periodically
  useEffect(() => {
    const refreshStatus = () => {
      setSyncStatus(getSyncStatus());
      setConflicts(getConflicts().filter(c => !c.resolved));
    };
    const interval = setInterval(refreshStatus, 5000);
    return () => clearInterval(interval);
  }, []);

  // Sync pending operations
  const syncAll = useCallback(async (silent = false) => {
    if (syncing || !navigator.onLine) return;
    
    const queue = getSyncQueue();
    if (queue.length === 0) {
      if (!silent) {
        toast.info('Nothing to sync', { description: 'All changes are already saved.' });
      }
      return;
    }
    
    setSyncing(true);
    
    let successCount = 0;
    let failCount = 0;
    
    for (const operation of queue) {
      try {
        const { method, endpoint, data } = operation;
        switch (method?.toUpperCase()) {
          case 'POST':
            await API.post(endpoint, data);
            break;
          case 'PUT':
            await API.put(endpoint, data);
            break;
          case 'PATCH':
            await API.patch(endpoint, data);
            break;
          case 'DELETE':
            await API.delete(endpoint);
            break;
          default:
            break;
        }
        // Remove successful operation from queue
        removeFromSyncQueue(operation.id);
        successCount++;
      } catch (error) {
        console.error('Sync operation failed:', error);
        failCount++;
      }
    }
    
    // Update last sync time
    if (successCount > 0) {
      updateLastSyncTime();
    }
    
    setSyncing(false);
    setSyncStatus(getSyncStatus());
    
    // Clean up resolved conflicts
    clearResolvedConflicts();
    setConflicts(getConflicts().filter(c => !c.resolved));
    
    // Show toast notification
    if (successCount > 0 && failCount === 0) {
      toast.success(`Synced ${successCount} change${successCount !== 1 ? 's' : ''}`, {
        description: 'All offline changes have been saved.',
        icon: <Check className="h-4 w-4 text-emerald-500" />
      });
    } else if (successCount > 0 && failCount > 0) {
      toast.warning(`Synced ${successCount}, ${failCount} failed`, {
        description: 'Some changes could not be saved. Will retry later.',
        icon: <AlertCircle className="h-4 w-4 text-amber-500" />
      });
    } else if (failCount > 0) {
      toast.error(`Failed to sync ${failCount} change${failCount !== 1 ? 's' : ''}`, {
        description: 'Check your connection and try again.',
        icon: <AlertCircle className="h-4 w-4 text-red-500" />
      });
    }
  }, [syncing]);
  
  // Handle conflict resolution
  const handleResolveConflict = async (conflict, strategy) => {
    resolveConflictInStorage(conflict.id, strategy);
    setConflicts(getConflicts().filter(c => !c.resolved));
    toast.success('Conflict resolved', { 
      description: strategy === 'client_wins' ? 'Your changes were kept.' : 'Server version was used.' 
    });
  };
  
  const handleResolveAllConflicts = async (strategy) => {
    resolveAllConflictsInStorage(strategy);
    setConflicts([]);
    toast.success('All conflicts resolved', { 
      description: strategy === 'client_wins' ? 'Your changes were kept.' : 'Server versions were used.' 
    });
  };
  
  const getStatusConfig = () => {
    // Check for conflicts first
    if (conflicts.length > 0) {
      return {
        icon: AlertTriangle,
        color: 'text-red-500',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        label: `${conflicts.length} Conflict${conflicts.length !== 1 ? 's' : ''}`,
        description: 'Tap to resolve sync conflicts',
        clickable: true,
        showButton: true,
        hasConflicts: true,
      };
    }
    
    if (!isOnline) {
      return {
        icon: CloudOff,
        color: 'text-slate-400',
        bgColor: 'bg-slate-100',
        borderColor: 'border-slate-200',
        label: 'Offline',
        description: 'Changes will sync when online',
        showButton: false,
      };
    }
    
    if (syncing) {
      return {
        icon: RefreshCw,
        color: 'text-blue-500',
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-200',
        label: 'Syncing...',
        description: 'Syncing your changes',
        animate: true,
        showButton: false,
      };
    }
    
    if (syncStatus.hasPendingChanges) {
      return {
        icon: AlertCircle,
        color: 'text-amber-500',
        bgColor: 'bg-amber-50',
        borderColor: 'border-amber-200',
        label: `${syncStatus.pendingOperations} Pending`,
        description: 'Tap to sync changes',
        clickable: true,
        showButton: true,
      };
    }
    
    return {
      icon: Check,
      color: 'text-emerald-500',
      bgColor: 'bg-emerald-50',
      borderColor: 'border-emerald-200',
      label: 'Synced',
      description: syncStatus.lastSyncTime 
        ? `Last synced ${formatTimeAgo(syncStatus.lastSyncTime)}`
        : 'All changes saved',
      showButton: false,
    };
  };
  
  const config = getStatusConfig();
  const Icon = config.icon;
  
  const sizeClasses = {
    sm: 'w-6 h-6',
    md: 'w-8 h-8',
    lg: 'w-10 h-10',
  };
  
  const iconSizeClasses = {
    sm: 'h-3.5 w-3.5',
    md: 'h-4 w-4',
    lg: 'h-5 w-5',
  };
  
  const handleClick = () => {
    if (config.hasConflicts) {
      setShowConflictDialog(true);
    } else if (config.clickable && isOnline) {
      syncAll();
    }
  };

  // Render "Sync Now" button variant
  if (variant === 'button' && config.showButton) {
    return (
      <>
        <Button
          variant="outline"
          size="sm"
          onClick={config.hasConflicts ? () => setShowConflictDialog(true) : () => syncAll()}
          disabled={syncing || !isOnline}
          className={cn(
            "gap-2",
            config.hasConflicts 
              ? "border-red-200 bg-red-50 text-red-700 hover:bg-red-100 hover:text-red-800"
              : "border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 hover:text-amber-800",
            className
          )}
          data-testid="sync-now-button"
        >
          {syncing ? (
            <RefreshCw className="h-4 w-4 animate-spin" />
          ) : config.hasConflicts ? (
            <AlertTriangle className="h-4 w-4" />
          ) : (
            <Upload className="h-4 w-4" />
          )}
          <span>{config.hasConflicts ? 'Resolve Conflicts' : 'Sync Now'}</span>
          <span className={cn(
            "px-1.5 py-0.5 rounded-full text-xs font-medium",
            config.hasConflicts ? "bg-red-200" : "bg-amber-200"
          )}>
            {config.hasConflicts ? conflicts.length : syncStatus.pendingOperations}
          </span>
        </Button>
        
        <ConflictResolutionDialog
          open={showConflictDialog}
          onOpenChange={setShowConflictDialog}
          conflicts={conflicts}
          onResolve={handleResolveConflict}
          onResolveAll={handleResolveAllConflicts}
        />
      </>
    );
  }

  // Render full variant with icon + label + button
  if (variant === 'full') {
    return (
      <>
        <div 
          className={cn(
            "flex items-center gap-3 px-3 py-2 rounded-lg border",
            config.bgColor,
            config.borderColor,
            className
          )}
          data-testid="sync-status-full"
        >
          <div 
            className={cn(
              "rounded-full flex items-center justify-center",
              sizeClasses[size],
              "bg-white/50"
            )}
          >
            <Icon 
              className={cn(
                iconSizeClasses[size],
                config.color,
                config.animate && "animate-spin"
              )}
            />
          </div>
          
          <div className="flex-1 min-w-0">
            <p className={cn("text-sm font-medium", config.color)}>
              {config.label}
            </p>
            <p className="text-xs text-slate-500 truncate">
              {config.description}
            </p>
          </div>
          
          {config.showButton && (
            <Button
              variant="ghost"
              size="sm"
              onClick={config.hasConflicts ? () => setShowConflictDialog(true) : () => syncAll()}
              disabled={syncing}
              className={cn(
                config.hasConflicts 
                  ? "text-red-600 hover:text-red-700 hover:bg-red-100"
                  : "text-amber-600 hover:text-amber-700 hover:bg-amber-100"
              )}
            >
              {syncing ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : config.hasConflicts ? (
                <>
                  <AlertTriangle className="h-4 w-4 mr-1" />
                  Resolve
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-1" />
                  Sync
                </>
              )}
            </Button>
          )}
        </div>
        
        <ConflictResolutionDialog
          open={showConflictDialog}
          onOpenChange={setShowConflictDialog}
          conflicts={conflicts}
          onResolve={handleResolveConflict}
          onResolveAll={handleResolveAllConflicts}
        />
      </>
    );
  }

  // Default icon variant
  return (
    <>
      <div 
        className={cn(
          "flex items-center gap-2",
          config.clickable && "cursor-pointer",
          className
        )}
        onClick={handleClick}
        title={config.description}
        data-testid="sync-status-indicator"
      >
        <div 
          className={cn(
            "rounded-full flex items-center justify-center transition-all",
            sizeClasses[size],
            config.bgColor,
            config.clickable && (config.hasConflicts ? "hover:ring-2 hover:ring-red-300" : "hover:ring-2 hover:ring-amber-300")
          )}
        >
          <Icon 
            className={cn(
              iconSizeClasses[size],
              config.color,
              config.animate && "animate-spin"
            )}
          />
        </div>
        
        {(showLabel || showSyncButton) && (
          <div className="flex items-center gap-2">
            {showLabel && (
              <div className="flex flex-col">
                <span className={cn("text-xs font-medium", config.color)}>
                  {config.label}
                </span>
                {size === 'lg' && (
                  <span className="text-[10px] text-slate-400">
                    {config.description}
                  </span>
                )}
              </div>
            )}
            
            {showSyncButton && config.showButton && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  if (config.hasConflicts) {
                    setShowConflictDialog(true);
                  } else {
                    syncAll();
                  }
                }}
                disabled={syncing}
                className={cn(
                  "h-7 px-2 text-xs",
                  config.hasConflicts 
                    ? "text-red-600 hover:text-red-700 hover:bg-red-100"
                    : "text-amber-600 hover:text-amber-700 hover:bg-amber-100"
                )}
              >
                {syncing ? (
                  <RefreshCw className="h-3 w-3 animate-spin" />
                ) : config.hasConflicts ? (
                  'Resolve'
                ) : (
                  'Sync'
                )}
              </Button>
            )}
          </div>
        )}
      </div>
      
      <ConflictResolutionDialog
        open={showConflictDialog}
        onOpenChange={setShowConflictDialog}
        conflicts={conflicts}
        onResolve={handleResolveConflict}
        onResolveAll={handleResolveAllConflicts}
      />
    </>
  );
};

/**
 * Format time ago string
 */
const formatTimeAgo = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
};

export default SyncStatusIndicator;
